import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ThemeProvider } from "@material-ui/core/styles";
import { theme } from '../../config/theme';
import { makeStyles } from "@material-ui/core/styles";
import { Container, TextField, Typography } from '@material-ui/core';
import LoadingButton from '../../components/LoadingButton.js'
import axios from 'axios';
import { toast } from "react-toastify";
import { TextMaskPhone } from '../../components/TextMaskCustom';

const useStyles = makeStyles((theme) => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    backgroundColor: '#EFEFEF',
    borderRadius: '16px',
    padding: '24px 14px 22px',
    boxSizing: 'border-box',
  },
  title: {
    fontWeight: 700,
    color: "#2D6E2F",
    textAlign: 'center',
    marginBottom: '14px',
  },
  wrapper: {
    width: '100%',
  },
  form: {
    width: '100%',
    borderRadius: '14px',
    backgroundColor: '#337D36',
    border: '1px solid #2A682C',
    padding: '18px 16px 16px',
    color: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    textAlign: 'left',
    boxSizing: 'border-box',
  },
  fieldLabel: {
    color: '#FFFFFF',
    fontSize: '1rem',
    marginBottom: '-4px',
  },
  field: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '8px',
      '& fieldset': {
        borderColor: '#D4D8E2',
      },
      '&:hover fieldset': {
        borderColor: '#BCD8BD',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1D3D1E',
      },
    },
    '& .MuiInputBase-input': {
      color: '#1A1A1A',
      fontWeight: 600,
    },
  },
  helperText: {
    color: '#FFFFFF',
    marginTop: '4px',
    minHeight: '20px',
  },
  saveButton: {
    marginTop: '6px',
    borderRadius: '8px',
    backgroundColor: '#225424',
    color: '#FFFFFF',
    fontWeight: 700,
    textTransform: 'none',
    minHeight: '52px',
    '&:hover': {
      backgroundColor: '#1C441D',
    },
  },
  removeLink: {
    display: 'block',
    marginTop: '4px',
    color: '#FFFFFF',
    opacity: 0.95,
    textDecoration: 'underline',
    fontSize: '0.95rem',
    textAlign: 'center',
    width: '100%',
  },
  smsHint: {
    color: '#E4F4E5',
    fontSize: '0.85rem',
    marginTop: '-4px',
  },
  confirmRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      '& > *': {
        width: '100%',
      },
    },
  },
  verifyButton: {
    borderRadius: '8px',
    backgroundColor: '#52B855',
    color: '#FFFFFF',
    fontWeight: 700,
    textTransform: 'none',
    minHeight: '56px',
    '&:hover': {
      backgroundColor: '#409E43',
    },
  },
}));

function normalizePhone(value) {
  return (value || '').replace(/\D/g, '');
}

export default function EditProfile(props) {
  const classes = useStyles();
  const [detailUser, setDetailUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/v1/customers/current', { params: { for_profile: true } })
      .then((res) => setDetailUser(res.data))
      .catch(() => {
        toast.error('Não foi possível carregar os dados completos do perfil.');
      })
      .finally(() => setProfileLoading(false));
  }, []);

  const sourceUser = detailUser || props?.user;

  const initialForm = useMemo(() => ({
    name: sourceUser?.name || sourceUser?.best_name || '',
    social_name: sourceUser?.social_name || '',
    cpf: sourceUser?.cpf_number || '',
    email: sourceUser?.email === '-' ? '' : (sourceUser?.email || ''),
    phone: sourceUser?.phone || sourceUser?.unconfirmed_phone || '',
  }), [sourceUser]);

  const [form, setForm] = useState(initialForm);
  const [token, setToken] = useState('');
  const [phoneConfirmationPending, setPhoneConfirmationPending] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingVerifyPhone, setLoadingVerifyPhone] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm(initialForm);
    setToken('');
    setPhoneConfirmationPending(false);
    setErrors({});
  }, [initialForm]);

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setLoadingSave(true);
    setErrors({});

    const initialPhone = normalizePhone(initialForm.phone);
    const currentPhone = normalizePhone(form.phone);
    const socialChanged = (form.social_name || '') !== (initialForm.social_name || '');
    const emailChanged = (form.email || '') !== (initialForm.email || '');
    const phoneChanged = currentPhone !== initialPhone;

    if (!socialChanged && !emailChanged && !phoneChanged) {
      toast.info('Nenhuma alteração para salvar.');
      setLoadingSave(false);
      return;
    }

    const requestErrors = {};

    try {
      if (socialChanged) {
        const socialData = new FormData();
        socialData.append('customer[social_name]', form.social_name || '');
        await axios.put('/api/v1/customers/update_social_name', socialData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      if (emailChanged) {
        const emailData = new FormData();
        emailData.append('customer[email]', form.email || '');
        await axios.put('/api/v1/customers/update_email', emailData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      if (phoneChanged) {
        await axios.put('/api/v1/phone/update_phone', {
          customer: { unconfirmed_phone: form.phone },
        });
        setPhoneConfirmationPending(true);
      } else {
        setPhoneConfirmationPending(false);
      }

      if (emailChanged && phoneChanged) {
        toast.success('Perfil atualizado. Confirme o telefone por SMS e verifique seu e-mail.');
      } else if (emailChanged) {
        toast.success('Perfil atualizado. Verifique seu e-mail para concluir a troca.');
      } else if (phoneChanged) {
        toast.success('SMS enviado. Digite o código para confirmar o novo telefone.');
      } else {
        toast.success('Perfil atualizado com sucesso.');
      }
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 401 || error.response?.status === 404) {
        Object.assign(requestErrors, error.response.data || {});
        setErrors(requestErrors);
      } else {
        toast.error('Erro ao processar solicitação.');
      }
    } finally {
      setLoadingSave(false);
    }
  }

  async function verifyPhoneToken(e) {
    e.preventDefault();
    if (!token) {
      setErrors((prev) => ({ ...prev, token: 'Preencha o token' }));
      return;
    }

    setLoadingVerifyPhone(true);
    setErrors((prev) => ({ ...prev, token: undefined }));
    try {
      await axios.post('/api/v1/phone/verify', { token });
      toast.success('Telefone alterado com sucesso!');
      setPhoneConfirmationPending(false);
      setTimeout(() => {
        window.location.href = "/site/change_profile";
      }, 1200);
    } catch (error) {
      if (error.response) {
        setErrors((prev) => ({ ...prev, token: error.response.data?.token || 'Token inválido' }));
      } else {
        toast.error('Erro ao verificar token.');
      }
    } finally {
      setLoadingVerifyPhone(false);
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.paper}>
        <div className={classes.page}>
          <Container maxWidth="sm" className={classes.wrapper}>
          <Typography variant="h5" className={classes.title}>
            Edite seu perfil
          </Typography>
            <form className={classes.form} onSubmit={handleSave}>
              <div className={classes.fieldLabel}>Seu nome:</div>
              <TextField
                className={classes.field}
                variant="outlined"
                fullWidth
                value={form.name}
                disabled
              />
              <div className={classes.fieldLabel}>Nome social:</div>
              <TextField
                className={classes.field}
                variant="outlined"
                fullWidth
                value={form.social_name}
                onChange={(e) => setField('social_name', e.target.value)}
                error={!!errors?.social_name}
                helperText={errors?.social_name || ''}
                FormHelperTextProps={{ className: classes.helperText }}
              />
              <div className={classes.fieldLabel}>CPF:</div>
              <TextField
                className={classes.field}
                variant="outlined"
                fullWidth
                value={form.cpf}
                disabled
              />
              <div className={classes.fieldLabel}>E-mail:</div>
              <TextField
                className={classes.field}
                variant="outlined"
                fullWidth
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                error={!!errors?.email}
                helperText={errors?.email || ''}
                FormHelperTextProps={{ className: classes.helperText }}
                autoComplete="email"
              />
              <div className={classes.fieldLabel}>Telefone:</div>
              <TextField
                className={classes.field}
                variant="outlined"
                fullWidth
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                error={!!errors?.unconfirmed_phone}
                helperText={errors?.unconfirmed_phone || ''}
                FormHelperTextProps={{ className: classes.helperText }}
                InputProps={{
                  inputComponent: TextMaskPhone,
                }}
              />

              {phoneConfirmationPending && (
                <>
                  <Typography className={classes.smsHint}>
                    Enviamos um SMS para confirmar seu novo telefone.
                  </Typography>
                  <div className={classes.confirmRow}>
                    <TextField
                      className={classes.field}
                      variant="outlined"
                      fullWidth
                      placeholder="Código SMS"
                      value={token}
                      onChange={(e) => setToken(e.target.value)}
                      error={!!errors?.token}
                      helperText={errors?.token || ''}
                      FormHelperTextProps={{ className: classes.helperText }}
                    />
                    <LoadingButton
                      className={classes.verifyButton}
                      variant="contained"
                      onClick={verifyPhoneToken}
                      loading={loadingVerifyPhone}
                    >
                      Confirmar
                    </LoadingButton>
                  </div>
                </>
              )}

              <LoadingButton
                className={classes.saveButton}
                fullWidth
                variant="contained"
                color="primary"
                loading={loadingSave}
                type="submit"
              >
                Atualizar perfil
              </LoadingButton>

              <Link className={classes.removeLink} to="/site/account_remove">
                Remover conta
              </Link>
            </form>
          </Container>
        </div>
      </div>
    </ThemeProvider>
  );
}
